import { useEffect, useState } from "react";

/** How long each warm-up line stays before the next one takes over. */
const LINE_ROTATE_MS = 2200;

/**
 * Warm-up lines for the quiet moment before the planner emits anything. Testing's own vocabulary —
 * what the agent is genuinely doing is reading the org — rather than Weave's weaving metaphor.
 */
const WARMUP_LINES = [
	"Reading your org…",
	"Looking up objects and fields…",
	"Checking record types…",
	"Working out what to click…",
	"Drafting the steps…",
] as const;

/**
 * The shimmering caption that sits inside a loading skeleton, so the wait reads as one animated
 * block instead of a static label stuck under a spinner. Hands the space over the moment real
 * activity arrives — pass `active={false}` and it renders nothing.
 *
 * Uses the shared `.shimmer-text` utility (globals.css), the same implementation Weave's warm-up
 * caption uses, so there is one gradient-text treatment in the product rather than two.
 */
function ShimmerCaption({ active }: { active: boolean }) {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		if (!active) return;
		const timer = setInterval(() => setIndex((n) => (n + 1) % WARMUP_LINES.length), LINE_ROTATE_MS);
		return () => clearInterval(timer);
	}, [active]);

	if (!active) return null;

	return (
		<div
			className="shimmer-text text-[13px] font-medium leading-[18px] tracking-[0.01em]"
			aria-live="polite"
		>
			{WARMUP_LINES[index]}
		</div>
	);
}

/**
 * The pre-response skeleton: a pulsing accent rule beside a shimmering caption and two shimmer rows.
 * Shown while a turn has been sent but nothing has streamed back yet.
 */
export function ThinkingSkeleton({ showCaption = true }: { showCaption?: boolean }) {
	return (
		<div className="flex items-start gap-3">
			<span
				aria-hidden
				data-tst-decorative-motion
				className="w-0.5 shrink-0 animate-pulse self-stretch rounded-sm bg-primary"
				style={{ minHeight: 56 }}
			/>
			<div className="flex min-w-0 flex-1 flex-col gap-2.5">
				<ShimmerCaption active={showCaption} />
				<div data-tst-decorative-motion className="h-2.5 w-5/6 animate-pulse rounded bg-muted" />
				<div data-tst-decorative-motion className="h-2.5 w-1/2 animate-pulse rounded bg-muted" />
			</div>
		</div>
	);
}
